import {
  DefaultAnswerEnum,
  ResponseTextSubTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { QuestionNode } from '~/domain/report/model/report/node/questionNode/questionNode'
import type { TextAnswer } from '~/domain/report/model/report/node/questionNode/textQuestionNode/textAnswer'
import type { TextQuestion } from '~/domain/report/model/report/node/questionNode/textQuestionNode/textQuestion'
import type { TextQuestionNode } from '~/domain/report/model/report/node/questionNode/textQuestionNode/textQuestionNode'
import { isNullish } from '~/utils/isNullish'
import { ApiToModelError } from '../error'
import type { ConversionContext } from './types'
import type {
  ReportNodeSchema,
  ReportQuestion,
} from '@ulysses-inc/harami_api_client'

/**
 * テキスト質問の API Schema を Store のデータ型に変換する
 *
 * @param context
 * @returns
 */
export const convertTextQuestionNode = (
  context: ConversionContext,
): QuestionNode => {
  const {
    question,
    base: { node, questionNodeBase, questionBase, recordedAt },
  } = context
  const textQuestion: TextQuestion = {
    ...questionBase,
    type: 'text',
    rule: {
      format: convertToFormatType(
        node.question?.responseTexts?.[0]?.subType,
        node,
      ),
    },
    defaultAnswer: convertToDefaultAnswer(question),
  }
  const textAnswer = convertToTextAnswer(
    question.responseAnswer,
    recordedAt,
    textQuestion.defaultAnswer,
  )

  const questionNode: TextQuestionNode = {
    ...questionNodeBase,
    questionType: 'text',
    question: textQuestion,
    answer: textAnswer,
  }

  return questionNode
}

const convertToFormatType = (
  subType: ResponseTextSubTypeEnum | undefined,
  node: ReportNodeSchema,
) => {
  if (subType === undefined) {
    throw new ApiToModelError('subType is undefined', {
      node,
    })
  }
  switch (subType) {
    case ResponseTextSubTypeEnum.SINGLE_LINE:
      return 'singleLine'
    case ResponseTextSubTypeEnum.MULTIPLE_LINE:
      return 'multipleLine'
  }
}

const convertToDefaultAnswer = (
  question: ReportQuestion,
): TextQuestion['defaultAnswer'] => {
  const defaultAnswer = question.responseTexts?.[0]?.defaultAnswer
  if (defaultAnswer) {
    return {
      type: defaultAnswer.type,
      value: defaultAnswer.value,
    }
  }
  return undefined
}

const convertToTextAnswer = (
  responseAnswer: ReportQuestion['responseAnswer'],
  recordedAt: Date | undefined = undefined,
  defaultAnswer: TextQuestion['defaultAnswer'],
): TextAnswer | undefined => {
  if (responseAnswer?.textValue?.textValue !== undefined) {
    return {
      type: 'text',
      value: responseAnswer.textValue.textValue,
      recordedAt,
    }
  } else {
    switch (defaultAnswer?.type) {
      // マスタから自動入力の場合、回答に値を設定する
      case DefaultAnswerEnum.ProductMaster:
        // 製品マスターに値がない場合は undefined を返す（NoMasterDataMessage を表示するため）
        if (isNullish(defaultAnswer.value) || defaultAnswer.value === '') {
          return undefined
        }
        return {
          type: 'text',
          value: defaultAnswer.value,
          recordedAt: undefined,
        }
      default:
        return undefined
    }
  }
}
